"use client";

import { useCallback, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import type {
  IngestThoughtsRequest,
  MindstringPrototype,
  SourcedMethod,
  SourcedWhere,
  SupportedLanguage,
} from "@/generated/fastapi";
import {
  showErrorToast,
  showSuccessToast,
  showWarningToast,
} from "@/shared/feedback";
import appSettings from "@/shared/config/appSettings";
import useAutoDetectedLanguage from "@/shared/language/useAutoDetectedLanguage";
import useIngestThoughtsMutation from "../mutations/useIngestThoughtsMutation";
import useThoughtRefresh from "./useThoughtRefresh";

const MINSTRING_MIN_LENGTH = appSettings.validation.MINSTRING_MIN_LENGTH;
const MINSTRING_MAX_LENGTH = appSettings.validation.MINSTRING_MAX_LENGTH;

const thoughtSchema = z.object({
  language: z.string().refine((val) => val !== "", {
    message: "Please select a language",
  }),
  thoughtText: z
    .string()
    .min(1, "Thought text is required")
    .refine(
      (text): text is string => {
        if (!text) return false;
        const mindstrings = text
          .split("\n")
          .map((line) => line.trim())
          .filter((line) => line.length > 0);

        return mindstrings.every(
          (mindstring) =>
            mindstring.length >= MINSTRING_MIN_LENGTH &&
            mindstring.length <= MINSTRING_MAX_LENGTH,
        );
      },
      (params) => ({
        message: (() => {
          const mindstrings = params
            .split("\n")
            .map((line) => line.trim())
            .filter((line) => line.length > 0);

          const tooShortMindstrings = mindstrings
            .map((mindstring, index) =>
              mindstring.length < MINSTRING_MIN_LENGTH ? index + 1 : null,
            )
            .filter((index): index is number => index !== null);

          const tooLongMindstrings = mindstrings
            .map((mindstring, index) =>
              mindstring.length > MINSTRING_MAX_LENGTH ? index + 1 : null,
            )
            .filter((index): index is number => index !== null);

          if (tooShortMindstrings.length > 0 && tooLongMindstrings.length > 0) {
            return `Some thoughts have invalid length. Thoughts #${tooShortMindstrings.join(
              ", ",
            )} are too short (min ${MINSTRING_MIN_LENGTH} chars) and thoughts #${tooLongMindstrings.join(
              ", ",
            )} are too long (max ${MINSTRING_MAX_LENGTH} chars)`;
          }

          if (tooShortMindstrings.length > 0) {
            return tooShortMindstrings.length === 1
              ? `Thought #${tooShortMindstrings[0]} is too short (minimum ${MINSTRING_MIN_LENGTH} characters)`
              : `Thoughts #${tooShortMindstrings.join(
                  ", ",
                )} are too short (minimum ${MINSTRING_MIN_LENGTH} characters)`;
          }

          if (tooLongMindstrings.length > 0) {
            return tooLongMindstrings.length === 1
              ? `Thought #${tooLongMindstrings[0]} is too long (maximum ${MINSTRING_MAX_LENGTH} characters)`
              : `Thoughts #${tooLongMindstrings.join(
                  ", ",
                )} are too long (maximum ${MINSTRING_MAX_LENGTH} characters)`;
          }

          return `Thoughts must be between ${MINSTRING_MIN_LENGTH} and ${MINSTRING_MAX_LENGTH} characters`;
        })(),
      }),
    ),
});

export type ThoughtFormValues = z.infer<typeof thoughtSchema>;

export default function useInsertThoughtsForm({
  situationId,
  prototype,
}: {
  situationId: string;
  prototype: MindstringPrototype;
}) {
  const ingestThoughtsMutation = useIngestThoughtsMutation();
  const { syncIngestedThoughts } = useThoughtRefresh();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isSubmitted },
    reset,
    control,
    setValue,
  } = useForm<ThoughtFormValues>({
    resolver: zodResolver(thoughtSchema),
    defaultValues: {
      language: "",
      thoughtText: "",
    },
    mode: "onSubmit",
  });

  const watchedLanguage = useWatch({
    control,
    name: "language",
    defaultValue: "",
  });

  const thoughtText = useWatch({
    control,
    name: "thoughtText",
    defaultValue: "",
  });

  const handleDetectedLanguageChange = useCallback(
    (nextLanguage: SupportedLanguage | "", reason: "manual" | "auto" | "reset") => {
      setValue("language", nextLanguage, {
        shouldValidate: isSubmitted,
        shouldDirty: reason === "manual",
        shouldTouch: reason === "manual",
      });
    },
    [isSubmitted, setValue],
  );

  const {
    languageItems,
    languageCodeToNameMap,
    detectedLanguage,
    autoDetectedLanguage,
    isDetectedLanguageSupported,
    selectedLanguages,
    handleLanguageSelection,
  } = useAutoDetectedLanguage({
    text: thoughtText,
    selectedLanguage: watchedLanguage,
    onLanguageChange: handleDetectedLanguageChange,
  });

  const mindstringsWithCounts = useMemo(() => {
    if (!thoughtText) return [];

    return thoughtText
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .map((mindstring, index) => ({
        text: mindstring,
        count: mindstring.length,
        index: index + 1,
        isValid:
          mindstring.length >= MINSTRING_MIN_LENGTH &&
          mindstring.length <= MINSTRING_MAX_LENGTH,
      }));
  }, [thoughtText]);

  const mindstringCount = useMemo(() => {
    if (!thoughtText) return 0;

    return thoughtText
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0).length;
  }, [thoughtText]);

  const onSubmit = async (data: ThoughtFormValues) => {
    const request: IngestThoughtsRequest = {
      situation_id: situationId,
      mindstrings: mindstringsWithCounts.map((mindstring) => mindstring.text),
      prototype,
      language: data.language as SupportedLanguage,
      sourced_where: "studio" as SourcedWhere,
      method: "manual_entry" as SourcedMethod,
      should_confirm: true,
    };

    try {
      const response = await ingestThoughtsMutation.mutateAsync(request);

      const submittedThoughtsCount = response.thoughts.length;
      if (response.status === "success") {
        showSuccessToast(
          `${submittedThoughtsCount} confirmed thoughts added to your profile.`,
        );
      } else if (response.status === "pending") {
        showWarningToast(
          `${submittedThoughtsCount} confirmed thoughts were saved and search is still catching up.`,
        );
      } else {
        showErrorToast(response.message || "Error adding thoughts");
        return;
      }

      reset();
      syncIngestedThoughts({
        response,
        shouldConfirm: request.should_confirm ?? false,
        prototype,
      });
    } catch {
      showErrorToast("Error adding thoughts");
    }
  };

  return {
    register,
    handleSubmit,
    errors,
    isSubmitting,
    watchedLanguage,
    selectedLanguages,
    thoughtText,
    detectedLanguage,
    autoDetectedLanguage,
    isDetectedLanguageSupported,
    mindstringsWithCounts,
    mindstringCount,
    onSubmit,
    handleLanguageSelection,
    languageItems,
    languageCodeToNameMap,
  };
}
